import { Fragment, type ReactNode } from "react";

/** "Page {n}" + { n: 3 } → "Page 3". Unknown placeholders are left as they are. */
export function fmt(s: string, vars: Record<string, string | number> = {}): string {
  return s.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** Pick the one/other form: plural(n, { one: "{n} payout", other: "{n} payouts" }). Languages without plurals repeat the same text. */
export function plural(n: number, forms: { one: string; other: string }, vars: Record<string, string | number> = {}): string {
  return fmt(n === 1 ? forms.one : forms.other, { n, ...vars });
}

type Tag = "b" | "code" | "a";
const DEFAULT_TAGS: Record<Tag, (children: string) => ReactNode> = {
  b: (c) => <strong>{c}</strong>,
  code: (c) => <code>{c}</code>,
  a: (c) => c,
};

/**
 * Copy with light markup ("Use <code>GET /v1/me</code>. <a>Read more</a>.") → React nodes. Tags don't nest.
 * Values are filled in after the markup is parsed, so a value (an org name, an email) can never add markup.
 */
export function rich(s: string, tags: Partial<Record<Tag, (children: string) => ReactNode>> = {}, vars?: Record<string, string | number>): ReactNode {
  const fill = (x: string) => (vars ? fmt(x, vars) : x);
  const out: ReactNode[] = [];
  const re = /<(b|code|a)>(.*?)<\/\1>/g;
  let last = 0;
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push(fill(s.slice(last, m.index)));
    const tag = m[1] as Tag;
    out.push(<Fragment key={m.index}>{(tags[tag] ?? DEFAULT_TAGS[tag])(fill(m[2] ?? ""))}</Fragment>);
    last = re.lastIndex;
  }
  if (last < s.length) out.push(fill(s.slice(last)));
  return out;
}
